// frontend/src/utils/labParameterStandardization.js
// Frontend utility for lab parameter standardization

import { labKeyToDisplayNameMap } from './labParameterMappings';

// Create reverse mapping: display name -> standardized parameter name
const displayNameToKeyMap = {};
Object.entries(labKeyToDisplayNameMap).forEach(([standardizedName, displayName]) => {
  displayNameToKeyMap[displayName] = standardizedName;
});

// Add additional legacy mappings for common variations
const additionalLegacyMappings = {
  'Hemoglobin A1c': 'labs_glucose_hba1c',
  'HbA1C': 'labs_glucose_hba1c', // Alternative case
  'Hemoglobin A1C': 'labs_glucose_hba1c', // Alternative case
  'A1c': 'labs_glucose_hba1c',
  'Glycated Hemoglobin': 'labs_glucose_hba1c',
  'LDL Cholesterol': 'labs_lipid_ldl',
  'HDL Cholesterol': 'labs_lipid_hdl',
  'Total Cholesterol': 'labs_lipid_chol_total',
  'Triglycerides': 'labs_lipid_trig',
  // Glucose variations
  'Fasting Glucose': 'labs_glucose_glucose',
  'Glucose': 'labs_glucose_glucose',
  // Hepatology/Liver function tests
  'AST': 'labs_hepatology_ast',
  'ALT': 'labs_hepatology_alt',
  'Alkaline Phosphatase': 'labs_hepatology_alp',
  'Bilirubin, Total': 'labs_hepatology_bilirubin_total',
  'Bilirubin, Direct': 'labs_hepatology_bilirubin_direct',
  'Albumin': 'labs_protein_albumin',
  'Total Protein': 'labs_protein_total_protein',
  // Thyroid tests
  'Free T3': 'labs_thyroid_free_t3',
  'White Blood Cell Count': 'labs_cbc_wbc',
  'Red Blood Cell Count': 'labs_cbc_rbc',
  'Platelet Count': 'labs_cbc_plt',
  // CRP hs variations
  'C-Reactive Protein, High Sensitivity': 'labs_inflammation_hscrp',
  'High-Sensitivity C-Reactive Protein': 'labs_inflammation_hscrp',
  'hs-CRP': 'labs_inflammation_hscrp',
  'CRP hs': 'labs_inflammation_hscrp',
  // Cholesterol variations
  'Cholesterol, Total': 'labs_lipid_chol_total',
  'Total Cholesterol': 'labs_lipid_chol_total',
  'Cholesterol Total': 'labs_lipid_chol_total',
  // TSH variations
  'TSH': 'labs_thyroid_tsh',
  'Thyroid Stimulating Hormone': 'labs_thyroid_tsh',
  'Thyrotropin': 'labs_thyroid_tsh',
  // Vitamin D variations
  'Vitamin D3': 'labs_vitamins_d3',
  'Vitamin D, 25-Hydroxy': 'labs_vitamins_d3',
  '25-Hydroxyvitamin D': 'labs_vitamins_d3',
  '25(OH)D': 'labs_vitamins_d3',
  '25 - OH Vitamin D': 'labs_vitamins_d3',
  '25-OH Vitamin D': 'labs_vitamins_d3',
  // Folate variations
  'Folate': 'labs_vitamins_b9',
  'Folic Acid': 'labs_vitamins_b9',
  'Vitamin B9': 'labs_vitamins_b9',
  'Serum Folate': 'labs_vitamins_b9',
  // BUN/Creatinine Ratio
  'BUN/Creatinine Ratio': 'labs_renal_bun_creat_ratio',
  // Atherogenic Index
  'Atherogenic Index': 'labs_lipid_athero_idx'
};

// Merge additional mappings
Object.assign(displayNameToKeyMap, additionalLegacyMappings);

/**
 * Convert display name to standardized parameter name
 * @param {string} displayName - User-friendly display name (e.g., "WBC")
 * @returns {string} - Standardized parameter name (e.g., "labs_cbc_wbc")
 */
export function displayNameToStandardized(displayName) {
  // First try exact match
  if (displayNameToKeyMap[displayName]) {
    return displayNameToKeyMap[displayName];
  }

  // Try case-insensitive match
  const exactMatch = Object.keys(displayNameToKeyMap).find(key =>
    key.toLowerCase() === displayName.toLowerCase()
  );
  if (exactMatch) {
    return displayNameToKeyMap[exactMatch];
  }

  // Try partial matches for common variations
  const partialMatch = Object.keys(displayNameToKeyMap).find(key => {
    const keyLower = key.toLowerCase();
    const displayLower = displayName.toLowerCase();

    // Handle common variations
    if (keyLower.includes(displayLower) || displayLower.includes(keyLower)) {
      return true;
    }

    // Handle specific cases
    if (displayLower === 'hba1c' && keyLower.includes('hba1c')) return true;
    if (displayLower === 'vitamin d' && keyLower.includes('vitamin d')) return true;
    if (displayLower === 'folate' && keyLower.includes('folate')) return true;
    if (displayLower === 'hscrp' && keyLower.includes('hscrp')) return true;
    if (displayLower === 'hs-crp' && keyLower.includes('hscrp')) return true;

    return false;
  });

  if (partialMatch) {
    return displayNameToKeyMap[partialMatch];
  }

  // Return original if no match found
  return displayName;
}

/**
 * Convert standardized parameter name to display name
 * @param {string} standardizedName - Standardized parameter name (e.g., "labs_cbc_wbc")
 * @returns {string} - User-friendly display name (e.g., "WBC")
 */
export function standardizedToDisplayName(standardizedName) {
  return labKeyToDisplayNameMap[standardizedName] || standardizedName;
}

/**
 * Validate if a parameter name follows the standardized convention
 * @param {string} parameterName - Parameter name to validate
 * @returns {boolean} - True if follows labs_<category>_<parameter> convention
 */
export function isStandardizedLabParameter(parameterName) {
  return /^labs_[a-z]+_[a-z0-9_]+$/.test(parameterName);
}

/**
 * Get parameter category from standardized name
 * @param {string} standardizedName - Standardized parameter name
 * @returns {string} - Category name extracted from parameter name
 */
export function getCategoryFromStandardizedName(standardizedName) {
  if (!isStandardizedLabParameter(standardizedName)) {
    return 'Lab Results'; // Default category
  }
  
  const parts = standardizedName.split('_');
  if (parts.length >= 3) {
    const categoryPart = parts[1]; // e.g., 'cbc' from 'labs_cbc_wbc'
    
    // Map category parts to proper category names
    const categoryMap = {
      'cbc': 'CBC',
      'coagulation': 'Coagulation',
      'glucose': 'Glucose',
      'lipid': 'Lipid',
      'electrolytes': 'Electrolytes',
      'vitamins': 'Vitamins',
      'renal': 'Renal',
      'inflammation': 'Inflammation',
      'hepatology': 'Hepatology',
      'protein': 'Protein',
      'thyroid': 'Thyroid',
      'urine': 'Urine'
    };
    
    return categoryMap[categoryPart] || 'Lab Results';
  }
  
  return 'Lab Results';
}

/**
 * Transform lab data for display (standardized names to display names)
 * @param {Array} labData - Array of lab data with standardized parameter names
 * @returns {Array} - Array of lab data with display names
 */
export function transformLabDataForDisplay(labData) {
  return labData.map(item => ({
    ...item,
    displayName: standardizedToDisplayName(item.name),
    testName: standardizedToDisplayName(item.name) // For backward compatibility
  }));
}

/**
 * Transform lab data to use standardized parameter names
 * @param {Array} labData - Array of lab data objects
 * @returns {Array} - Array of lab data with standardized parameter names
 */
export function transformLabDataToStandardized(labData) {
  return labData.map(item => ({
    ...item,
    name: displayNameToStandardized(item.testName || item.name)
  }));
}

/**
 * Get all standardized lab parameter names
 * @returns {Array} - Array of all standardized parameter names
 */
export function getAllStandardizedParameterNames() {
  return Object.keys(labKeyToDisplayNameMap);
}

/**
 * Get all display names
 * @returns {Array} - Array of all display names
 */
export function getAllDisplayNames() {
  return Object.values(labKeyToDisplayNameMap);
}

export { labKeyToDisplayNameMap, displayNameToKeyMap };
